import * as axios from '../../axios-extended'
import { useOrganization } from '@library/react-toolkit'
import { flow, pipe } from 'fp-ts/lib/function'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import { throwLeft } from '../../axios-extended'
import { useShowError, useShowSuccess } from '../Notifications'
import {
  createOrganizationInvoice,
  createSupplierInvoice,
  deleteBill,
  updateBill,
  getBill,
  getBills,
  getPublishedBills,
  getSupplierBills,
  publishBill,
  unpublishBill,
  getSmartHubInvoice,
  getFinancingRequest,
  sendSupplierBills,
  getRegisteredEmail,
  getOrganizationPublicInfo,
} from './api'
import {
  PublishedInvoice,
  Query,
  setAddModalVisible,
  setModeAction,
  setSelectedBillsAction,
} from './types'
import { useDispatch, useSelector } from 'react-redux'
import { array, taskEither } from 'fp-ts'
import {
  InvoiceWithTotal,
  NewOrganizationInvoice,
  NewSupplierInvoice,
} from '../../types'

export const useOrganizationBills = (req: Partial<Query>) => {
  const org = useOrganization()
  const showError = useShowError()
  const mode = useMode()

  return useQuery(
    [org?.name, `bills`, req],
    pipe(getBills(org?.name ?? '', req), throwLeft),
    {
      enabled: !!org?.name && mode === 'organization',
      keepPreviousData: true,
      staleTime: 60 * 1000,
      onError: e => {
        console.debug('supplier invoices error', e)
        showError('getInvoicesError')
      },
    }
  )
}

export const useRegisteredEmail = (organization: string) => {
  const org = useOrganization()

  return useQuery(
    [org?.name, `registered-email`, organization],
    pipe(getRegisteredEmail(org?.name ?? '', organization), throwLeft),
    {
      enabled: !!org?.name && !!organization,
      keepPreviousData: true,
      staleTime: 60 * 1000,
      onError: e => {
        console.debug('registered email error', e)
      },
    }
  )
}

export const useSupplierBills = (req: Partial<Query>) => {
  const org = useOrganization()
  const showError = useShowError()
  const mode = useMode()

  return useQuery(
    [org?.name, `supplier-bills`, req],
    pipe(getSupplierBills(org?.name ?? '', req), throwLeft),
    {
      enabled: !!org?.name && mode === 'supplier',
      keepPreviousData: true,
      staleTime: 60 * 1000,
      onError: e => {
        console.debug('supplier invoices error', e)
        showError('getInvoicesError')
      },
    }
  )
}

export const useBills = (req: Partial<Query>) => {
  const org = useOrganizationBills(req)
  const supplier = useSupplierBills(req)
  const mode = useMode()

  return mode === 'organization' ? org : supplier
}

export const useBill = (id: number) => {
  const showError = useShowError()

  return useQuery(['bill', id], pipe(getBill(id), throwLeft), {
    enabled: !isNaN(id),
    onError: () => {
      showError('getInvoiceError')
    },
  })
}

export const useAddOrganizationInvoice = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (invoice: NewOrganizationInvoice) =>
      pipe(createOrganizationInvoice(org?.name ?? '', invoice), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `bills`])
      },
      onError: () => showError('addInvoiceError'),
    }
  )
}

export const useAddSupplierInvoice = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (invoice: NewSupplierInvoice) =>
      pipe(createSupplierInvoice(org?.name ?? '', invoice), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `supplier-bills`])
      },
      onError: () => showError('addInvoiceError'),
    }
  )
}

export const useDeleteBill = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (id: number) => pipe(deleteBill(org?.name ?? '', id), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `bills`])
      },
      onError: () => showError('deleteInvoiceError'),
    }
  )
}

export const useUpdateBill = (id?: number) => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (bill: NewOrganizationInvoice) =>
      pipe(updateBill(org?.name ?? '', id ?? 0, bill), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `bills`])
      },
      onError: () => showError('editInvoiceError'),
    }
  )
}

export const usePublishInvoice = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()
  const showSuccess = useShowSuccess()

  return useMutation(
    (req: PublishedInvoice) =>
      pipe(publishBill(org?.name ?? '', req), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`publish-bill`)
        queryClient.invalidateQueries([org?.name, `published-bills`])
        showSuccess('publishInvoiceSuccess')
      },
      onError: e => {
        console.log(e)
        showError('publishInvoiceError')
      },
    }
  )
}

export const usePublishInvoices = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()
  const showSuccess = useShowSuccess()

  return useMutation(
    (reqs: PublishedInvoice[]) =>
      pipe(
        reqs,
        array.traverse(taskEither.Monad)(req =>
          publishBill(org?.name ?? '', req)
        ),
        throwLeft
      )(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`publish-bill`),
          queryClient.invalidateQueries([org?.name, `published-bills`]),
          showSuccess('publishInvoiceSuccess')
      },
      onError: e => {
        console.log(e)
        showError('publishInvoiceError')
      },
    }
  )
}

export const usePublishedInvoices = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useQuery(
    [org?.name, 'published-bills'],
    pipe(getPublishedBills(org?.name ?? ''), throwLeft),
    {
      enabled: !!org?.name,
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `bills`])
      },
      onError: () => showError('publishInvoiceError'),
    }
  )
}

export const useUnpublishInvoice = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()
  const showSuccess = useShowSuccess()

  return useMutation(
    (invoiceID: number) =>
      pipe(unpublishBill(org?.name ?? '', invoiceID), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, 'published-bills']),
          showSuccess('unPublishInvoiceSuccess')
      },

      onError: () => showError('unPublishInvoiceError'),
    }
  )
}

export const useMode = () => useSelector(s => s.bills.mode)

export const useSetMode = () => {
  const dispatch = useDispatch()

  return flow(setModeAction, dispatch)
}

export const useSelectedBills = () =>
  useSelector(({ bills: { mode, selectedBills } }) => selectedBills[mode])

export const useSetSelectedBills = () => {
  const dispatch = useDispatch()

  return flow(setSelectedBillsAction, dispatch)
}

export const useSmartHubInvoice = (organization?: string, invoiceID?: number) =>
  useQuery(
    [organization, 'smart-hub-invoice', invoiceID],
    pipe(
      getSmartHubInvoice(organization ?? '', invoiceID ?? 0),
      axios.catchNotFound,
      throwLeft
    ),
    {
      enabled: !!organization && !!invoiceID,
    }
  )

export const useSmartHubInvoices = (invoices: InvoiceWithTotal[]) =>
  useQueries(
    invoices.map(({ id, organization }) => ({
      queryKey: [organization, 'smart-hub-invoice', id],
      queryFn: pipe(
        getSmartHubInvoice(organization, id ?? 0),
        axios.catchNotFound,
        throwLeft
      ),
      enabled: !!organization,
    }))
  )

export const useSendSupplierBills = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()
  const showSuccess = useShowSuccess()

  return useMutation(
    (ids: number[]) =>
      pipe(sendSupplierBills(org?.name ?? '', ids), throwLeft)(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([org?.name, `supplier-bills`])
        showSuccess('notificationSuccess')
      },
      onError: e => {
        console.log(e)
        showError('notificationError')
      },
    }
  )
}

export const useFinancingRequest = (
  organization?: string,
  invoiceID?: number
) =>
  useQuery(
    [organization, 'smart-hub-request', invoiceID],
    pipe(
      getFinancingRequest(organization ?? '', invoiceID ?? 0),
      axios.catchNotFound,
      throwLeft
    ),
    {
      enabled: !!organization && !!invoiceID,
    }
  )

export const useModalVisible = (): [boolean, (_: boolean) => void] => {
  const dispatch = useDispatch()
  const visible = useSelector(s => s.bills.addModalVisible)

  return [visible, flow(setAddModalVisible, dispatch)]
}

export const useOrganizationPublicInfo = (org: string) => {
  return useQuery(
    [org, `public-info`],
    pipe(getOrganizationPublicInfo(org), throwLeft),
    {
      enabled: org !== '',
    }
  )
}
